<template>
  <b-container class="h-100">
    <b-row class="h-100 justify-content-center align-items-center">
      <b-col cols="12" md="8" lg="8">
        <h3 class="font-weight-bold mb-2">Two-factor authentication</h3>
        <p class="text-muted mb-4">
          Enter the five digit code sent to your phone to finish signing in.
        </p>

        <b-alert v-if="error" variant="danger" show>{{ error }}</b-alert>
        <b-alert v-if="notice" variant="info" show>{{ notice }}</b-alert>

        <code-input
          class="mb-4"
          :loading="loading"
          :disabled="attemptsExhausted"
          :key="codeInputKey"
          focus-on-mount
          @complete="handleComplete"
        />

        <div class="d-flex justify-content-between align-items-center">
          <b-button
            variant="link"
            class="px-0"
            :disabled="loading || (cooldown > 0 && !attemptsExhausted)"
            @click="resend"
          >
            Resend code
            <span v-if="cooldown > 0 && !attemptsExhausted"
              >({{ cooldown }}s)</span
            >
          </b-button>

          <b-button
            variant="outline-danger"
            :disabled="loading"
            @click="cancel"
          >
            Cancel
          </b-button>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import CodeInput from '@/components/inputs/CodeInput'

const RESEND_COOLDOWN_SECONDS = 30

export default {
  components: {
    CodeInput,
  },

  data() {
    return {
      loading: false,
      error: null,
      notice: null,
      cooldown: 0,
      cooldownTimer: null,
      attemptsExhausted: false,
      codeInputKey: 0,
    }
  },

  computed: {
    ...mapState('auth', {
      token: (state) => state.token,
      mfa: (state) => state.mfa,
      user: (state) => state.user,
    }),

    channelLabel() {
      const channel = this.mfa && this.mfa.channel
      if (!channel) {
        return 'registered contact'
      }
      return channel === 'sms' ? 'mobile number' : 'email'
    },

    hasAccessToken() {
      return Boolean(this.token && this.token.access_token)
    },
  },

  created() {
    if (!this.hasAccessToken) {
      this.$router.replace({ name: 'auth.login-page' })
      return
    }

    if (!this.mfa || !this.mfa.required) {
      this.redirectAfterVerification()
      return
    }

    this.startCooldown()
  },

  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    }
  },

  methods: {
    ...mapActions('auth', [
      'sendMfa',
      'verifyMfa',
      'logout',
      'getAuthenticatedUser',
    ]),

    startCooldown(seconds = RESEND_COOLDOWN_SECONDS) {
      this.cooldown = seconds
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
      }
      this.cooldownTimer = setInterval(() => {
        if (this.cooldown <= 1) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
          this.cooldown = 0
        } else {
          this.cooldown -= 1
        }
      }, 1000)
    },

    async resend() {
      if ((this.cooldown > 0 && !this.attemptsExhausted) || this.loading) return

      this.loading = true
      this.error = null
      this.notice = null

      try {
        await this.sendMfa()
        this.attemptsExhausted = false
        this.codeInputKey += 1
        this.notice = 'A new verification code has been sent.'
        this.startCooldown()
      } catch (err) {
        const response = err && err.response && err.response.data
        this.error =
          (response && response.message) ||
          'Unable to send a new code. Please try again shortly.'
      } finally {
        this.loading = false
      }
    },

    async handleComplete(code) {
      if (this.loading || !code || this.attemptsExhausted) return

      this.loading = true
      this.error = null
      this.notice = null
      const tokenId = this.mfa && this.mfa.token_id

      try {
        // include token_id if we have one; otherwise omit and rely on current access token
        const payload = await this.verifyMfa(
          tokenId ? { otp: code, token_id: tokenId } : { otp: code },
        )

        // On success, refresh user and move forward regardless of flags
        await this.getAuthenticatedUser().catch(() => {})
        this.redirectAfterVerification()
        return payload
      } catch (err) {
        const response = err && err.response && err.response.data
        const message = response && response.message
        const attempts = response && response.attempts_remaining
        const status = err && err.response && err.response.status
        if (status === 423 || attempts === 0) {
          this.attemptsExhausted = true
          this.error =
            message ||
            'Too many incorrect attempts. Please resend a new verification code.'
        } else if (typeof attempts === 'number') {
          this.error = `${
            message || 'Invalid or expired code.'
          } ${attempts} attempt(s) remaining.`
        } else {
          this.error = message || 'Invalid or expired code.'
        }
      } finally {
        this.loading = false
      }
    },

    redirectAfterVerification() {
      if (!this.hasAccessToken) {
        this.$router.replace({ name: 'auth.login-page' })
        return
      }

      const user = this.user || {}
      if (user.user_type === 'business_admin' && user.first_login) {
        this.$router
          .replace({ name: 'auth.change-password-page' })
          .catch(() => {})
      } else {
        this.$router.replace({ name: 'home.welcome-page' }).catch(() => {})
      }
    },

    async cancel() {
      await this.logout()
    },
  },
}
</script>

<style scoped>
.b-container {
  min-height: 60vh;
}
</style>
